import { useForm } from "react-hook-form";
import Joi from "joi";
import { joiResolver } from "@hookform/resolvers/joi";
import TextInput from "../TextInput/TextInput";
import {
  Title,
  Description,
  Form,
  Wrapper,
  Card,
} from "./CompanyDataStep.elements";
import Checkbox, { CheckboxLink } from "../Checkbox/Checkbox";
import { useTranslation, Trans } from "react-i18next";
import { ButtonPrimary } from "../Button/Button";

export type CompanyDataStepProps = {
  companyName: string;
  setCompanyName?: (name: string) => void;
  regNumber: string;
  setRegNumber?: (reg: string) => void;
  taxNumber: string;
  setTaxNumber?: (tax: string) => void;
  accepted?: boolean;
  setAccepted?: (val: boolean) => void;
  onFinish: (data: any) => void;
};

export default function CompanyDataStep({
  companyName,
  setCompanyName,
  regNumber,
  setRegNumber,
  taxNumber,
  setTaxNumber,
  accepted = false,
  setAccepted,
  onFinish,
}: CompanyDataStepProps) {
  const { t, i18n } = useTranslation();
  const schema = Joi.object({
    companyName: Joi.string().min(3).required(),
    regNumber: Joi.string().min(3).required(),
    taxNumber: Joi.string().min(3).required(),
    accepted: Joi.boolean().valid(true).required(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    formState: { isValid },
  } = useForm({
    mode: "onSubmit",
    resolver: joiResolver(schema),
    defaultValues: {
      companyName,
      regNumber,
      taxNumber,
      accepted,
    },
  });

  const acceptedValue = watch("accepted", accepted);

  const lang = i18n.language as "hu" | "en" | "lv";
  const links = {
    hu: {
      pp: "https://data.blockben.com/gdpr/Privacy_Policy_hu.pdf",
      toc: "https://data.blockben.com/terms/A_BlockWallet_GTC_hu.pdf",
    },
    en: {
      pp: "https://data.blockben.com/gdpr/Privacy_Policy_en.pdf",
      toc: "https://data.blockben.com/terms/A_BlockWallet_GTC_en.pdf",
    },
    lv: {
      pp: "https://data.blockben.com/gdpr/Privacy_Policy_lv.pdf",
      toc: "https://data.blockben.com/terms/A_BlockWallet_GTC_lv.pdf",
    },
  };
  const currentLinks = links[lang];

  return (
    <Wrapper>
      <Card>
        <Title>{t("registration.companyDataStep.title")}</Title>
        <Description>
          {t("registration.companyDataStep.description")}
        </Description>
        <Form onSubmit={handleSubmit(onFinish)}>
          <TextInput
            label={t("registration.companyDataStep.companyNameLabel")}
            name="companyName"
            register={register}
            placeholder={t(
              "registration.companyDataStep.companyNamePlaceholder"
            )}
            onChange={(val) => {
              clearErrors("companyName");
              setCompanyName && setCompanyName(val);
            }}
          />
          <TextInput
            label={t("registration.companyDataStep.regNumberLabel")}
            name="regNumber"
            register={register}
            placeholder={t("registration.companyDataStep.regNumberPlaceholder")}
            onChange={(val) => {
              clearErrors("regNumber");
              setRegNumber && setRegNumber(val);
            }}
          />
          <TextInput
            label={t("registration.companyDataStep.taxNumberLabel")}
            name="taxNumber"
            register={register}
            placeholder={t("registration.companyDataStep.taxNumberPlaceholder")}
            onChange={(val) => {
              clearErrors("taxNumber");
              setTaxNumber && setTaxNumber(val);
            }}
          />
          <Checkbox
            id="accepted"
            checked={!!acceptedValue}
            onChange={(e) => {
              setValue("accepted", e.target.checked, { shouldValidate: true });
              setAccepted && setAccepted(e.target.checked);
              clearErrors("accepted");
            }}
            required
            label={
              <Trans
                i18nKey="registration.companyDataStep.checkboxLabel"
                components={{
                  pp: <CheckboxLink href={currentLinks.pp} target="_blank" />,
                  toc: <CheckboxLink href={currentLinks.toc} target="_blank" />,
                }}
              />
            }
          />
          <ButtonPrimary
            disabled={!isValid || !accepted}
            onClick={handleSubmit(onFinish)}
            style={{
              width: "fit-content",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            {t("registration.companyDataStep.submit")}
          </ButtonPrimary>
        </Form>
      </Card>
    </Wrapper>
  );
}
